import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, useTheme, IconButton, Paper, alpha } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import { Delete } from 'react-iconly';
import CustomDialog from '@ui/components/common/Dialog';
import BDrawer from '@ui/components/common/BDrawer';
import { useForm, useFieldArray, useWatch } from 'react-hook-form';
import ControlledHCheckboxAutocomplete from '@ui/components/common/Autocomplete/ControlledHCheckboxAutocomplete';
import SingleSelect from '@ui/components/common/Autocomplete/SingleSelect';
import { centeredRowStack } from '@ui/style/style';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { getAllPeopleWithoutPaginationAsync, getReferredByPersonByMvpIdAsync } from '@application/features/people/peopleThunk';
import { getAllExternalRelationsWithoutPaginationAsync } from '@application/features/externalRelations/externalRelationsThunk';
import { createMVPAsync, createExternalMVPsAsync, updateExternalMVPAsync } from '@application/features/mvps/mvpsThunk';
import { MVPTypeEnum } from '@application/enums/mvp-type-enum';
import { ControlledFilledTextField } from '@ui/components/common/TextField';
import { getEventLanguagesByEventIdAsync } from '@application/features/languages/languagesThunk';
const SelectMVPsDialog = ({ open, onClose, isSmallScreen, type, mvpToEdit, eventId, onSuccess }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const allPeople = useAppSelector((state) => state.people.allPeople);
    const allPeopleLoading = useAppSelector((state) => state.people.allPeopleLoading);
    const referredByPerson = useAppSelector((state) => state.people.referredByPerson);
    const allExternalRelations = useAppSelector((state) => state.externalRelations.allExternalRelations);
    const { eventLanguages } = useAppSelector((state) => state.languages);
    const isEditMode = !!mvpToEdit;
    const { control, handleSubmit, reset, setValue, formState: { errors } } = useForm({
        defaultValues: {
            mvpBoxes: [
                {
                    selectedMVPs: type === 'internal' ? [] : null,
                    referredByPersonId: null,
                    languageId: null
                }
            ],
            email: undefined,
            name: undefined
        }
    });
    useEffect(() => {
        if (open && eventId) {
            dispatch(getEventLanguagesByEventIdAsync(eventId));
        }
    }, [open, eventId, dispatch]);
    const languageOptions = React.useMemo(() => (eventLanguages || []).map((lang) => ({
        label: lang.name,
        value: lang.id,
        icon: lang.flagUrl ? (_jsx(Box, { component: 'img', src: lang.flagUrl, alt: lang.name, sx: { width: 20, height: 15, objectFit: 'cover', borderRadius: 0.5 } })) : undefined
    })), [eventLanguages]);
    // Default the first box to the event's primary language for a fresh "add" - edit mode gets
    // its value from the pre-populate effect below instead, so it doesn't get clobbered by this.
    useEffect(() => {
        if (open && !mvpToEdit && eventLanguages && eventLanguages.length > 0) {
            const primary = eventLanguages.find((l) => l.isPrimary) || eventLanguages[0];
            setValue('mvpBoxes.0.languageId', primary.id);
        }
    }, [open, mvpToEdit, eventLanguages, setValue]);
    const defaultLanguageId = () => {
        const primary = eventLanguages?.find((l) => l.isPrimary) || eventLanguages?.[0];
        return primary?.id ?? null;
    };
    const { fields, append, remove } = useFieldArray({
        control,
        name: 'mvpBoxes'
    });
    // Watch all mvpBoxes to track selected users across all rows
    const watchedMvpBoxes = useWatch({
        control,
        name: 'mvpBoxes'
    });
    const searchTimerRef = useRef(null);
    const referredByTimerRef = useRef(null);
    const [peopleSearchInput, setPeopleSearchInput] = useState('');
    const [referredByInput, setReferredByInput] = useState('');
    const prevInternalSelectionSigRef = useRef(null);
    const handleSearchChange = useCallback((value) => {
        if (searchTimerRef.current)
            clearTimeout(searchTimerRef.current);
        searchTimerRef.current = setTimeout(() => {
            const trimmed = value.trim();
            if (trimmed.length >= 3) {
                dispatch(getAllPeopleWithoutPaginationAsync({ eventId, searchString: trimmed }));
            }
            else if (trimmed.length === 0) {
                dispatch(getAllPeopleWithoutPaginationAsync({ eventId, searchString: '' }));
            }
        }, 500);
    }, [dispatch, eventId]);
    const handlePeopleInputChange = useCallback((_, value, reason) => {
        if (reason === 'input' || reason === 'clear') {
            setPeopleSearchInput(value);
        }
        if (reason === 'reset') {
            return;
        }
        if (reason === 'clear') {
            handleSearchChange('');
            return;
        }
        if (reason === 'input') {
            handleSearchChange(value);
        }
    }, [handleSearchChange]);
    const handleReferredByInputChange = useCallback((_, value, reason) => {
        if (reason === 'reset') {
            setReferredByInput(value);
            return;
        }
        if (reason === 'input' || reason === 'clear') {
            setReferredByInput(value);
        }
        if (referredByTimerRef.current)
            clearTimeout(referredByTimerRef.current);
        referredByTimerRef.current = setTimeout(() => {
            const trimmed = value.trim();
            if (trimmed.length >= 3) {
                dispatch(getAllPeopleWithoutPaginationAsync({ searchString: trimmed }));
            }
            else if (trimmed.length === 0) {
                dispatch(getAllPeopleWithoutPaginationAsync({ searchString: '' }));
            }
        }, 500);
    }, [dispatch]);
    const filterInternalMvpOptions = useCallback((options, state) => {
        const q = state.inputValue.trim().toLowerCase();
        if (!q)
            return options;
        return options.filter((option) => {
            const labelMatch = option.label?.toLowerCase().includes(q);
            const emailMatch = option.email?.toLowerCase().includes(q);
            const designationMatch = String(option.designation ?? '')
                .toLowerCase()
                .includes(q);
            const companyMatch = option.companyName?.toLowerCase().includes(q);
            return labelMatch || emailMatch || designationMatch || companyMatch;
        });
    }, []);
    useEffect(() => {
        return () => {
            if (searchTimerRef.current) {
                clearTimeout(searchTimerRef.current);
                searchTimerRef.current = null;
            }
            if (referredByTimerRef.current) {
                clearTimeout(referredByTimerRef.current);
                referredByTimerRef.current = null;
            }
        };
    }, []);
    useEffect(() => {
        if (!open) {
            prevInternalSelectionSigRef.current = null;
            setPeopleSearchInput('');
            setReferredByInput('');
            return;
        }
        if (type !== 'internal')
            return;
        const sig = JSON.stringify((watchedMvpBoxes ?? []).map((box) => Array.isArray(box.selectedMVPs)
            ? box.selectedMVPs.map((x) => x.value).sort()
            : []));
        if (prevInternalSelectionSigRef.current !== null &&
            prevInternalSelectionSigRef.current !== sig) {
            setPeopleSearchInput('');
        }
        prevInternalSelectionSigRef.current = sig;
    }, [open, type, watchedMvpBoxes]);
    // Fetch people when dialog opens for internal type
    useEffect(() => {
        if (open && type === 'internal') {
            dispatch(getAllPeopleWithoutPaginationAsync({ eventId }));
        }
    }, [open, type, dispatch, eventId]);
    // Fetch external relations and people when dialog opens for external type
    useEffect(() => {
        if (open && type === 'external') {
            dispatch(getAllExternalRelationsWithoutPaginationAsync(eventId));
            dispatch(getAllPeopleWithoutPaginationAsync({ searchString: '' }));
        }
    }, [open, type, dispatch, eventId]);
    // Fetch the referred-by person by MVP ID on edit so they appear in the dropdown
    useEffect(() => {
        if (open && type === 'external' && mvpToEdit?.id) {
            dispatch(getReferredByPersonByMvpIdAsync(mvpToEdit.id));
        }
    }, [open, type, mvpToEdit?.id, dispatch]);
    // Once referredByPerson arrives from API, inject it directly into the form
    // and sync the controlled input — this runs after the main reset() so it wins.
    useEffect(() => {
        if (!open || !isEditMode || type !== 'external' || !referredByPerson)
            return;
        setValue('mvpBoxes.0.referredByPersonId', referredByPerson.value);
        setReferredByInput(referredByPerson.label || '');
    }, [referredByPerson, open, isEditMode, type, setValue]);
    // Convert people to options for dropdown (internal)
    // Convert external relations to options for dropdown (external)
    const mvpOptions = React.useMemo(() => {
        if (type === 'internal' && allPeople) {
            return allPeople.map((person) => ({
                label: person.name || '',
                value: person.id,
                email: person.email,
                designation: person.position,
                companyName: person.companyName || person.peopleCompany
            }));
        }
        if (type === 'external' && allExternalRelations) {
            return allExternalRelations.map((relation) => ({
                label: relation.name || `${relation.firstName} ${relation.lastName}`.trim() || '',
                value: relation.id,
                email: relation.email,
                designation: relation.designation,
                companyName: relation.company
            }));
        }
        return [];
    }, [type, allPeople, allExternalRelations]);
    // Convert people to options for "Referred By" dropdown (external only)
    // Merge in the referredByPerson fetched by MVP ID so the pre-selected value
    // is always present even when it falls outside the first 10 results.
    const referredByOptions = React.useMemo(() => {
        if (type !== 'external')
            return [];
        const base = (allPeople ?? []).map((person) => ({
            label: person.name || '',
            value: person.id,
            email: person.email,
            designation: person.position,
            companyName: person.companyName || person.peopleCompany
        }));
        if (referredByPerson && !base.some((o) => o.value === referredByPerson.value)) {
            base.unshift({
                label: referredByPerson.label || '',
                value: referredByPerson.value,
                email: referredByPerson.email,
                designation: referredByPerson.designation,
                companyName: referredByPerson.companyName
            });
        }
        return base;
    }, [type, allPeople, referredByPerson]);
    useEffect(() => {
        if (!open || mvpToEdit)
            return;
        setPeopleSearchInput('');
        reset({
            mvpBoxes: [
                {
                    selectedMVPs: type === 'internal' ? [] : null,
                    referredByPersonId: null,
                    languageId: defaultLanguageId()
                }
            ]
        });
    }, [open, mvpToEdit, type, reset]);
    // Immediately populate email and name fields when opening in edit mode,
    // before external relations finish loading.
    useEffect(() => {
        if (mvpToEdit && open) {
            setValue('email', mvpToEdit.email ?? '');
            setValue('name', `${mvpToEdit.firstName ?? ''} ${mvpToEdit.lastName ?? ''}`.trim());
            setValue('mvpBoxes.0.languageId', mvpToEdit.preferredLanguage?.id ?? null);
        }
    }, [mvpToEdit, open, type, setValue]);
    // Pre-populate form when editing
    useEffect(() => {
        if (mvpToEdit && open && mvpOptions.length > 0) {
            if (type === 'external') {
                // For external, find the external relation by matching name/email
                const externalRelation = allExternalRelations?.find((er) => er.firstName === mvpToEdit.firstName &&
                    er.lastName === mvpToEdit.lastName &&
                    er.email === mvpToEdit.email);
                // Use referredByPerson from Redux if already fetched, so reset doesn't overwrite it
                const referredByPersonId = referredByPerson?.value ?? null;
                if (externalRelation) {
                    reset({
                        mvpBoxes: [
                            {
                                selectedMVPs: externalRelation.id,
                                referredByPersonId: referredByPersonId,
                                languageId: mvpToEdit.preferredLanguage?.id ?? null
                            }
                        ],
                        email: mvpToEdit.email,
                        name: `${mvpToEdit.firstName} ${mvpToEdit.lastName}`
                    });
                }
                else {
                    reset({
                        mvpBoxes: [
                            {
                                selectedMVPs: null,
                                referredByPersonId: referredByPersonId,
                                languageId: mvpToEdit.preferredLanguage?.id ?? null
                            }
                        ],
                        email: mvpToEdit.email,
                        name: `${mvpToEdit.firstName} ${mvpToEdit.lastName}`
                    });
                }
            }
            else if (isEditMode) {
                // Internal MVPs are tied to an existing Person - only the language can be edited,
                // so there's no picker selection to restore, just the language.
                reset({
                    mvpBoxes: [
                        {
                            selectedMVPs: [],
                            referredByPersonId: null,
                            languageId: mvpToEdit.preferredLanguage?.id ?? null
                        }
                    ],
                    email: mvpToEdit.email,
                    name: `${mvpToEdit.firstName} ${mvpToEdit.lastName}`
                });
            }
        }
    }, [mvpToEdit, open, reset, mvpOptions, type, allExternalRelations, allPeople, setValue, referredByPerson]);
    const handleAdd = async (data) => {
        if (data.mvpBoxes && data.mvpBoxes.length > 0) {
            if (isEditMode) {
                try {
                    if (mvpToEdit) {
                        // External RSVPs can also have their Referred By changed; internal RSVPs are tied to
                        // an existing Person, so only the language is editable there.
                        const box = data.mvpBoxes[0];
                        const updateRequest = {
                            id: mvpToEdit.id,
                            referredByPersonId: type === 'external' ? box.referredByPersonId || null : undefined,
                            preferredLanguageId: box.languageId || null
                        };
                        await dispatch(updateExternalMVPAsync(updateRequest)).unwrap();
                        toast.success(t('events.eventDetails.rsvpTracking.rsvpUpdatedSuccessfully') ||
                            'RSVP updated successfully!');
                        onClose();
                        reset({
                            mvpBoxes: [
                                {
                                    selectedMVPs: type === 'internal' ? [] : null,
                                    referredByPersonId: null,
                                    languageId: defaultLanguageId()
                                }
                            ]
                        });
                        if (onSuccess) {
                            onSuccess();
                        }
                    }
                }
                catch (error) {
                    let errorMessage = t('events.eventDetails.rsvpTracking.failedToUpdateRSVP') ||
                        'Failed to update RSVP. Please try again.';
                    if (error) {
                        if (error.error && typeof error.error === 'string') {
                            errorMessage = error.error;
                        }
                        else if (error.message && typeof error.message === 'string') {
                            errorMessage = error.message;
                        }
                    }
                    toast.error(errorMessage);
                }
            }
            else {
                try {
                    if (type === 'internal') {
                        // Process all boxes and create internal MVPs
                        let hasSelectedItems = false;
                        for (const box of data.mvpBoxes) {
                            // ControlledHCheckboxAutocomplete returns an array of option objects
                            const selectedMVPsArray = Array.isArray(box.selectedMVPs) ? box.selectedMVPs : [];
                            if (selectedMVPsArray.length > 0) {
                                hasSelectedItems = true;
                                const selectedIds = selectedMVPsArray.map((mvp) => mvp.value);
                                const createRequest = {
                                    eventId,
                                    type: MVPTypeEnum.Internal,
                                    personId: selectedIds,
                                    preferredLanguageId: box.languageId || undefined
                                };
                                await dispatch(createMVPAsync(createRequest)).unwrap();
                            }
                        }
                        if (!hasSelectedItems) {
                            toast.error(t('events.eventDetails.rsvpTracking.selectAtLeastOneRSVP') ||
                                'Please select at least one RSVP.');
                            return;
                        }
                    }
                    else {
                        // Process all boxes and create external MVPs in one request
                        const externalMVPs = [];
                        for (const box of data.mvpBoxes) {
                            // SingleSelect returns a string value, not an array
                            const selectedMVPValue = typeof box.selectedMVPs === 'string' && box.selectedMVPs ? box.selectedMVPs : null;
                            if (selectedMVPValue) {
                                externalMVPs.push({
                                    externalRelationId: selectedMVPValue,
                                    referredByPersonId: box.referredByPersonId || null,
                                    preferredLanguageId: box.languageId || null
                                });
                            }
                        }
                        if (externalMVPs.length > 0) {
                            const createExternalRequest = {
                                eventId,
                                mvps: externalMVPs
                            };
                            await dispatch(createExternalMVPsAsync(createExternalRequest)).unwrap();
                        }
                        else {
                            toast.error(t('events.eventDetails.rsvpTracking.selectAtLeastOneRSVP') ||
                                'Please select at least one RSVP.');
                            return;
                        }
                    }
                    toast.success(t('events.eventDetails.rsvpTracking.rsvpCreatedSuccessfully') ||
                        'RSVP created successfully!');
                    onClose();
                    reset({
                        mvpBoxes: [
                            {
                                selectedMVPs: type === 'internal' ? [] : null,
                                referredByPersonId: null,
                                languageId: defaultLanguageId()
                            }
                        ]
                    });
                    if (onSuccess) {
                        onSuccess();
                    }
                }
                catch (error) {
                    // Extract error message from backend API response
                    let errorMessage = t('events.eventDetails.rsvpTracking.failedToCreateRSVP') ||
                        'Failed to create RSVP. Please try again.';
                    if (error) {
                        if (error.error && typeof error.error === 'string') {
                            errorMessage = error.error;
                        }
                        else if (error.message && typeof error.message === 'string') {
                            errorMessage = error.message;
                        }
                    }
                    toast.error(errorMessage);
                }
            }
        }
    };
    const handleCancel = () => {
        reset({
            mvpBoxes: [
                {
                    selectedMVPs: type === 'internal' ? [] : null,
                    referredByPersonId: null,
                    languageId: defaultLanguageId()
                }
            ]
        });
        onClose();
    };
    const handleAddMore = () => {
        append({
            selectedMVPs: type === 'internal' ? [] : null,
            referredByPersonId: null,
            languageId: defaultLanguageId()
        });
    };
    const handleRemove = (index) => {
        if (fields.length > 1) {
            remove(index);
        }
        else {
            // If it's the last box, just reset it instead of removing
            reset({
                mvpBoxes: [
                    {
                        selectedMVPs: type === 'internal' ? [] : null,
                        referredByPersonId: null,
                        languageId: defaultLanguageId()
                    }
                ]
            });
        }
    };
    const dialogContent = (_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column' }, children: [_jsx(Box, { sx: {
                    maxHeight: 'calc(100dvh - 330px)',
                    overflowY: isSmallScreen ? 'hidden' : 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                    p: 3,
                    '&::-webkit-scrollbar': {
                        width: '8px'
                    },
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: 'transparent'
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: theme.palette.grey[600],
                        borderRadius: '4px',
                        '&:hover': {
                            backgroundColor: theme.palette.grey[500]
                        }
                    }
                }, children: fields.map((field, index) => (_jsxs(Paper, { elevation: 0, sx: {
                        borderRadius: theme.spacing(2),
                        padding: theme.spacing(2),
                        backgroundColor: 'transparent',
                        position: 'relative',
                        boxShadow: '0px 12px 24px -4px #919EAB1F, 0px 0px 2px 0px #919EAB33'
                    }, children: [fields.length > 1 && !isEditMode && (_jsx(IconButton, { onClick: () => handleRemove(index), size: 'small', sx: {
                                position: 'absolute',
                                top: -15,
                                right: -15,
                                zIndex: 1,
                                backgroundColor: alpha(theme.palette.error.main, 0.12),
                                color: theme.palette.error.main,
                                '&:hover': {
                                    backgroundColor: alpha(theme.palette.error.main, 0.2)
                                }
                            }, children: _jsx(Delete, { size: 20 }) })), _jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1, pt: 1 }, children: [type === 'internal' && !isEditMode && (_jsx(ControlledHCheckboxAutocomplete, { bg: 'grey8', control: control, label: t('events.eventDetails.rsvpTracking.selectRSVPs') || 'Select RSVPs', name: `mvpBoxes.${index}.selectedMVPs`, options: mvpOptions, error: errors.mvpBoxes?.[index]?.selectedMVPs?.message, isSortable: true, loading: allPeopleLoading, inputValue: peopleSearchInput, onInputChange: handlePeopleInputChange, filterOptions: filterInternalMvpOptions })), type === 'internal' && isEditMode && (_jsx(ControlledFilledTextField, { bg: 'grey8', control: control, name: 'email', label: t('labels.email'), disabled: true })), type === 'internal' && languageOptions.length > 0 && (_jsx(SingleSelect, { bg: 'grey8', control: control, label: t('events.eventDetails.rsvpTracking.invitationLanguage') ||
                                        'Invitation Language', name: `mvpBoxes.${index}.languageId`, options: languageOptions, showSelectedIcon: true, rules: {
                                        required: t('validations.required', {
                                            field: t('events.eventDetails.rsvpTracking.invitationLanguage') ||
                                                'Invitation Language'
                                        })
                                    }, error: errors.mvpBoxes?.[index]?.languageId?.message })), type === 'external' && (_jsxs(_Fragment, { children: [!isEditMode && (() => {
                                            // Get all selected user IDs from other rows (excluding current row)
                                            const selectedUserIds = watchedMvpBoxes
                                                ?.map((box, idx) => (idx !== index ? box.selectedMVPs : null))
                                                .filter((value) => typeof value === 'string' && value !== null && value !== '') || [];
                                            // Filter options to exclude users selected in other rows
                                            // But keep the current row's selected user in the options (so user can see their current selection)
                                            const currentRowValue = watchedMvpBoxes?.[index]?.selectedMVPs;
                                            const filteredOptions = mvpOptions.filter((option) => !selectedUserIds.includes(option.value) ||
                                                option.value === currentRowValue);
                                            return (_jsx(SingleSelect, { bg: 'grey8', control: control, label: t('events.eventDetails.rsvpTracking.selectRSVPs') || 'Select RSVPs', name: `mvpBoxes.${index}.selectedMVPs`, options: filteredOptions, error: errors.mvpBoxes?.[index]?.selectedMVPs?.message }));
                                        })(), isEditMode && (_jsx(_Fragment, { children: _jsx(ControlledFilledTextField, { bg: 'grey8', control: control, name: 'email', label: t('labels.email'), disabled: isEditMode }) })), _jsx(SingleSelect, { bg: 'grey8', control: control, label: t('events.eventDetails.rsvpTracking.referredBy') || 'Referred By', name: `mvpBoxes.${index}.referredByPersonId`, options: referredByOptions, error: errors.mvpBoxes?.[index]?.referredByPersonId?.message, placeholder: t('events.eventDetails.rsvpTracking.selectPerson') || 'Select Person', ...(isEditMode && { inputValue: referredByInput }), onInputChange: handleReferredByInputChange }), languageOptions.length > 0 && (_jsx(SingleSelect, { bg: 'grey8', control: control, label: t('events.eventDetails.rsvpTracking.invitationLanguage') ||
                                                'Invitation Language', name: `mvpBoxes.${index}.languageId`, options: languageOptions, showSelectedIcon: true, rules: {
                                                required: t('validations.required', {
                                                    field: t('events.eventDetails.rsvpTracking.invitationLanguage') ||
                                                        'Invitation Language'
                                                })
                                            }, error: errors.mvpBoxes?.[index]?.languageId?.message }))] }))] })] }, field.id))) }), !isEditMode && (_jsx(Box, { px: 3, pt: 1, children: _jsx(Button, { variant: 'outlined', color: 'primary', onClick: handleAddMore, startIcon: _jsx(Icon, { icon: 'eva:plus-fill', width: 20 }), sx: {
                        alignSelf: 'flex-start',
                        textTransform: 'none'
                    }, children: t('events.eventDetails.rsvpTracking.addMore') || 'Add more' }) }))] }));
    const actionButtons = (_jsxs(Box, { sx: { ...centeredRowStack }, width: '100%', gap: 1, children: [_jsx(Button, { fullWidth: true, variant: 'outlined', color: 'inherit', onClick: handleCancel, size: 'large', children: t('actions.cancel') || 'Cancel' }), _jsx(Button, { fullWidth: true, variant: 'contained', color: 'inherit', onClick: handleSubmit(handleAdd), size: 'large', sx: {
                    backgroundColor: theme.palette.grey[800],
                    '&:hover': {
                        backgroundColor: theme.palette.grey[700]
                    }
                }, children: isEditMode ? t('actions.save') || 'Save' : t('actions.addNow') || 'Add now' })] }));
    return (_jsxs(_Fragment, { children: [!isSmallScreen && (_jsx(CustomDialog, { backgroundVariant: 'white', open: open, onClose: handleCancel, titleWithIcon: isEditMode
                    ? t('events.eventDetails.rsvpTracking.editRSVP') || 'Edit RSVP'
                    : type === 'internal'
                        ? t('events.eventDetails.rsvpTracking.addInternalRSVP') || 'Add Internal RSVP'
                        : t('events.eventDetails.rsvpTracking.addExternalRSVP') || 'Add External RSVP', titleIcon: '/assets/images/add-person.png', actions: actionButtons, children: dialogContent })), isSmallScreen && (_jsx(BDrawer, { anchor: 'bottom', title: isEditMode
                    ? t('events.eventDetails.rsvpTracking.editRSVP') || 'Edit RSVP'
                    : type === 'internal'
                        ? t('events.eventDetails.rsvpTracking.addInternalRSVP') || 'Add Internal RSVP'
                        : t('events.eventDetails.rsvpTracking.addExternalRSVP') || 'Add External RSVP', open: open, onClose: handleCancel, showCloseButton: true, dialogActions: actionButtons, children: _jsx(Box, { sx: { flex: 1, overflow: 'auto' }, children: dialogContent }) }))] }));
};
export default SelectMVPsDialog;
